<template>
  <q-page
    padding
    class="main-wrapper-page single-page services-page"
    :class="{ 'design-open': isServiceDesignOpen }"
    style="background-color: var(--q-accent);"
  >
    <div v-if="initLoad" class="loading">
      <q-spinner-gears color="primary" size="6em" />
    </div>
    <PageView :pageView="pageView" @changeView="changePageView" />
    <CatalogFilter
      v-model="filters"
      @update="updFilters"
      :categories="categories"
      :canReset="canReset"
      :mode="pageView"
      @resetFilters="resetFilters"
    />
    <transition-group
      v-if="catalogServices && catalogServices.length && pageView === 'grid'"
      appear
      enter-active-class="animated fadeIn"
    >
      <template v-for="(service, key) in catalogServices" :key="key">
        <div
          v-if="
            (service.infraServices &&
              service.infraServices.length &&
              service.infraServices.filter((infra) => infra.isCatalogPart).length) ||
            hasInfraServices(service)
          "
          class="service-holder q-mb-lg q-px-md q-py-md bg-white"
        >
          <div class="heading-service flex justify-between content-center">
            <q-toolbar-title :ref="service.categoryId" class="q-my-none text-weight-bold"
              >{{ service.name }}
              <sup v-if="service.description">
                <q-btn
                  push
                  flat
                  round
                  size="sm"
                  color="white"
                  text-color="text-color"
                  class="q-px-none"
                  icon="mdi-information-outline"
                >
                  <q-popup-proxy>
                    <q-banner class="service-description service-reasons" v-html="$options.marked(service.description)">
                    </q-banner>
                  </q-popup-proxy>
                </q-btn>
              </sup>
            </q-toolbar-title>
            <div class="btn-group">
              <q-btn
                @click="goToService(service, 'tree')"
                class="service-action-btn"
                icon="account_tree"
                outline
                round
                :disable="!uiConfig?.showTreeAssistant"
                size="12px"
                v-ripple="{ color: 'primary' }"
              >
                <q-tooltip>Decision Tree</q-tooltip>
              </q-btn>
              <q-btn
                @click="goToService(service, 'wizard')"
                class="service-action-btn"
                icon="mdi-stairs"
                outline
                round
                :disable="!uiConfig?.showTreeAssistant"
                size="12px"
                v-ripple="{ color: 'primary' }"
              >
                <q-tooltip>Step by step<br />assistant</q-tooltip>
              </q-btn>
              <q-btn
                v-if="isAuth || userInfo.applicationsInOwn.length"
                @click="goToNewService(service)"
                class="service-action-btn"
                icon="add"
                outline
                round
                size="12px"
                v-ripple="{ color: 'primary' }"
              >
                <q-tooltip>Add Infrastructure Service</q-tooltip>
              </q-btn>
            </div>
          </div>
          <ServiceCategory :service="service" :design="design" />
        </div>
      </template>
    </transition-group>
    <div class="widget-section" v-if="catalogServices && catalogServices.length && pageView === 'list'">
      <q-table
        :rows="filteredInfraServices"
        :columns="cols"
        flat
        class="external-dns-table"
        hide-bottom
        @row-click="rowClick"
        :pagination="{
          rowsPerPage: 0,
        }"
      >
        <template v-slot:body-cell-cloudType="props">
          <td>
            <span>{{ props.row.cloudType.join(', ') || 'n/a' }}</span>
          </td>
        </template>
        <template v-slot:body-cell-categoryName="props">
          <q-td :props="props">
            <div class="long-desc">
              {{ props.row.categoryName }}
              <q-tooltip v-if="props.row.categoryName.length > 45"
                ><span>{{ props.row.categoryName }}<br /></span
              ></q-tooltip>
            </div>
          </q-td>
        </template>
        <template v-slot:body-cell-actions="props">
          <td>
            <q-btn
              v-if="notCanBeDeleted(props.row)"
              class="btn--no-hover"
              size="12px"
              style="cursor: not-allowed"
              icon="lock"
              flat
              padding="0"
              :ripple="false"
            >
              <q-tooltip>Can not be removed from service design</q-tooltip>
            </q-btn>
            <span v-else>
              <q-spinner v-if="addServiceLoadId === props.row.id" size="1.5em" class="q-mr-xs"></q-spinner>
              <q-btn
                v-else-if="props.row.orderable ? !isInDesign(props.row) : false"
                @click.stop="addToDesign(props.row)"
                class="btn--no-hover"
                :icon="isInDesign(props.row) ? 'check' : 'add'"
                flat
                :disable="
                  !currentService ||
                  serviceErrors[props.row.id] ||
                  uiConfig?.disableEditServices?.includes(props.row.apmid) ||
                  uiConfig?.hideServices?.includes(props.row.apmid)
                "
                padding="0"
                :ripple="false"
              >
                <q-tooltip v-if="serviceErrors[props.row.id]">{{ serviceErrors[props.row.id] }}</q-tooltip>
                <q-tooltip v-else-if="!currentService">Please choose digital service</q-tooltip>
                <q-tooltip v-else-if="!isInDesign(props.row)">Add to Service Design</q-tooltip>
              </q-btn>
              <q-btn
                v-else-if="props.row.orderable ? isInDesign(props.row) : false"
                @click.stop="removeFromServiceDesign(props.row)"
                class="btn--no-hover"
                icon="remove"
                flat
                padding="0"
                :ripple="false"
              >
                <q-tooltip>Remove from Service Design</q-tooltip>
              </q-btn>
            </span>
          </td>
        </template>
      </q-table>
    </div>
  </q-page>
</template>

<script>
import { marked } from 'marked';
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { scroll } from 'quasar';
import { SERVICES_ACTIONS, SERVICES_GETTERS } from '@/store/modules/services';
import { SERVICE_DESIGN_ACTIONS, SERVICE_DESIGN_GETTERS } from '@/store/modules/service-design';
import { DIGITAL_SERVICES_GETTERS } from '@/store/modules/digital-services';
import { BANNERS_GETTERS } from '@/store/modules/banners';
import { APP_ACTIONS, APP_GETTERS } from '@/store/modules/app';
import { getUserPermissions } from '@/pages/digitalService/api';
import ServiceCategory from './components/ServiceCategory.vue';
import PageView from './components/PageView.vue';
import CatalogFilter from './components/CatalogFilter.vue';
import * as uiConfig from '@/shared/ui-config.json';
import { preCreateDraft } from '@/pages/digitalService/api';

const { getScrollTarget, setVerticalScrollPosition } = scroll;

export default {
  name: 'ServiceCatalog',
  components: { ServiceCategory, PageView, CatalogFilter },
  marked,
  data() {
    return {
      uiConfig: {},
      isServiceDesignOpen: false,
      isAuth: false,
      initLoad: false,
      title: this.$route.meta.title,
      breadcrumbs: [],
      filters: {
        nameOrSubtitle: undefined,
        state: undefined,
        cloudTypes: [],
        categories: [],
        orderable: false,
      },
      pageView: 'grid',
      cols: [
        {
          label: 'Name',
          name: 'displayName',
          field: 'displayName',
          align: 'left',
          sortable: true,
        },
        {
          label: 'APMID',
          name: 'apmid',
          field: 'apmid',
          align: 'left',
          sortable: true,
        },
        {
          label: 'Category',
          name: 'categoryName',
          field: 'categoryName',
          align: 'left',
          sortable: true,
        },
        {
          label: 'Cloud type',
          name: 'cloudType',
          field: 'cloudType',
          align: 'left',
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'left',
        },
      ],
      addServiceLoadId: undefined,
      serviceErrors: {},
      nameFilterTimeout: null,
    };
  },
  created() {
    const savedView = localStorage.getItem('pageView');
    if (savedView) {
      this.pageView = savedView;
    }
    emitter.on('isServiceDesignOpen', (flag) => {
      this.isServiceDesignOpen = flag;
    });
    emitter.on('goToCategory', (apmId) => {
      const element = this.$refs[apmId][0].$el;
      const target = getScrollTarget(element);
      const offset = element.offsetTop - (this.currentBanner ? 95 : 45);
      const duration = 1000;
      setVerticalScrollPosition(target, offset, duration);
    });
  },

  async mounted() {
    this.uiConfig = uiConfig[process.env.VUE_APP_ENV];

    // Load filters from URL parameters
    this.loadFiltersFromURL();

    const hasUrlFilters =
      this.filters.nameOrSubtitle ||
      this.filters.state ||
      this.filters.cloudTypes.length ||
      this.filters.categories.length ||
      this.filters.orderable;

    if (hasUrlFilters) {
      await this.filterServices();
    } else {
      await this.load();
    }
    const authData = {
      userEmail: this.userInfo.email,
      scope: {},
      resource: {
        type: 'management',
        name: 'service.catalog.manage',
      },
    };
    this.isAuth = await getUserPermissions(authData);
    this.breadcrumbs = this.$route.meta.breadcrumbs;
  },
  unmounted() {
    emitter.off('goToCategory');
    emitter.off('isServiceDesignOpen');
    // Clean up timeout to prevent memory leaks
    if (this.nameFilterTimeout) {
      clearTimeout(this.nameFilterTimeout);
    }
  },
  computed: {
    ...mapGetters('services', {
      catalogServices: SERVICES_GETTERS.ALL,
      wizardCategories: SERVICES_GETTERS.WIZARD_CATEGORIES,
      catalogCategories: SERVICES_GETTERS.CATEGORIES,
    }),
    ...mapGetters('digitalServices', {
      currentService: DIGITAL_SERVICES_GETTERS.CURRENT_SERVICE,
    }),
    ...mapGetters('serviceDesign', {
      design: SERVICE_DESIGN_GETTERS.ALL,
    }),
    ...mapGetters('banners', {
      currentBanner: BANNERS_GETTERS.CURRENT,
    }),
    ...mapGetters('app', {
      userInfo: APP_GETTERS.USER_INFO,
    }),
    canReset() {
      return this.filters.state || this.filters.cloudTypes.length || this.filters.orderable;
    },
    categories() {
      return this.catalogCategories.map((i) => i.name);
    },
    filteredInfraServices() {
      return this.allInfraServices.filter((item) => {
        const matchState = !this.filters.state || this.filters.state === item.state;
        const matchCloudType =
          this.filters.cloudTypes.length === 0 || item.cloudType.some((ct) => this.filters.cloudTypes.includes(ct));
        const matchCategory =
          this.filters.categories.length === 0 ||
          this.filters.categories.some((cat) => item.categoryName === cat || item.categoryName.startsWith(cat));
        const matchOrderable = !this.filters.orderable || item.orderable === true;
        const matchNameOrSubtitle =
          !this.filters.nameOrSubtitle ||
          (item.displayName && item.displayName.toLowerCase().includes(this.filters.nameOrSubtitle.toLowerCase())) ||
          (item.subtitle && item.subtitle.toLowerCase().includes(this.filters.nameOrSubtitle.toLowerCase()));

        return matchState && matchCategory && matchCloudType && matchOrderable && matchNameOrSubtitle;
      });
    },
    allInfraServices() {
      const allInfraServices = [];
      this.catalogServices.forEach((category) => {
        if (category?.infraServices.length) {
          category.infraServices.forEach((infra) => {
            if (infra.isCatalogPart) {
              infra.categoryName = category.name;
              infra.categoryId = category.categoryId;
              allInfraServices.push(infra);
            }
          });
        }
        if (category?.serviceCategories.length) {
          category.serviceCategories.forEach((subCategory) => {
            if (subCategory.infraServices.length) {
              subCategory.infraServices.forEach((infra) => {
                if (infra.isCatalogPart) {
                  infra.categoryName = category.name + '. ' + subCategory.name;
                  infra.categoryId = category.categoryId;
                  allInfraServices.push(infra);
                }
              });
            }
          });
        }
      });
      return allInfraServices;
    },
  },
  methods: {
    ...mapActions('services', {
      getCatalog: SERVICES_ACTIONS.GET_SERVICES,
    }),
    ...mapActions('app', {
      setEditGroup: APP_ACTIONS.SET_INFRA_SERVICE_EDIT_GROUP,
    }),
    ...mapActions('serviceDesign', {
      setInfraService: SERVICE_DESIGN_ACTIONS.SET_INFRASERVICE,
      removeInfraService: SERVICE_DESIGN_ACTIONS.REMOVE_INFRASERVICE,
    }),
    rowClick(target, service) {
      this.$router.push(`/services/${service.id}`);
    },
    changePageView(view) {
      this.resetFilters();
      this.pageView = view;
      localStorage.setItem('pageView', view); // Save the view to local storage
    },
    notCanBeDeleted(service) {
      const category = this.design.find((category) => category.categoryId === service.categoryId);
      return category.infraServices.some((infra) => infra.apmid === service.apmid && infra.notCanBeDeleted);
    },
    isInDesign(service) {
      const category = this.design.find((category) => category.categoryId === service.categoryId);
      return category ? category.infraServices.some((infra) => infra.apmid === service.apmid) : false;
    },
    async addToDesign(service) {
      this.addServiceLoadId = service.id;
      try {
        const data = {
          infraServiceApmId: service.apmid,
          digitalServiceApmId: this.currentService.apmId,
        };
        await preCreateDraft(data);
        this.setInfraService({ category: { categoryId: service.categoryId }, infraService: service });
        this.$notify(
          'info',
          'Info',
          `${
            service.displayName || service.serviceName
          } was added to your service design. Please choose digital service if you would like to onboard onto it.`,
        );
      } catch (error) {
        this.serviceErrors[service.id] = error.message;
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.addServiceLoadId = undefined;
      }
    },
    removeFromServiceDesign(service) {
      this.removeInfraService({ category: { categoryId: service.categoryId }, infraService: service });
      this.$notify(
        'info',
        'Info',
        `${this.dataService.displayName || this.dataService.serviceName} was removed from your service design`,
      );
    },
    goToNewService(category) {
      this.$router.push({ name: 'newInfraService', params: { category: category.categoryId } });
    },
    hasInfraServices(service) {
      return (
        service.serviceCategories &&
        service.serviceCategories.length &&
        !!service.serviceCategories.filter((category) => category.infraServices.length).length
      );
    },
    updFilters(data) {
      const { type, value } = data;
      this.filters[type] = value;

      // Handle debouncing for nameOrSubtitle filter
      if (type === 'nameOrSubtitle') {
        // Clear existing timeout
        if (this.nameFilterTimeout) {
          clearTimeout(this.nameFilterTimeout);
        }

        // Set new timeout for name filtering
        this.nameFilterTimeout = setTimeout(() => {
          this.updateURL();
          if (this.pageView === 'grid') {
            this.filterServices();
          }
        }, 500); // 500ms delay
      } else {
        // For other filters, update immediately
        this.updateURL();
        if (this.pageView === 'grid') {
          this.filterServices();
        }
      }
    },
    resetFilters() {
      this.filters.nameOrSubtitle = undefined;
      this.filters.state = undefined;
      this.filters.cloudTypes = [];
      this.filters.categories = [];
      this.filters.orderable = false;
      this.updateURL();
      this.filterServices();
    },
    loadFiltersFromURL() {
      const query = this.$route.query;

      if (query.nameOrSubtitle) {
        this.filters.nameOrSubtitle = query.nameOrSubtitle;
      }

      if (query.state) {
        this.filters.state = query.state;
      }

      if (query.cloudTypes) {
        this.filters.cloudTypes = Array.isArray(query.cloudTypes) ? query.cloudTypes : query.cloudTypes.split(',');
      }

      if (query.categories) {
        this.filters.categories = Array.isArray(query.categories) ? query.categories : query.categories.split(',');
      }

      if (query.orderable !== undefined) {
        this.filters.orderable = query.orderable === 'true';
      }
    },
    updateURL() {
      const query = {};

      if (this.filters.nameOrSubtitle) {
        query.nameOrSubtitle = this.filters.nameOrSubtitle;
      }

      if (this.filters.state) {
        query.state = this.filters.state;
      }

      if (this.filters.cloudTypes && this.filters.cloudTypes.length > 0) {
        query.cloudTypes = this.filters.cloudTypes;
      }

      if (this.filters.categories && this.filters.categories.length > 0) {
        query.categories = this.filters.categories;
      }

      if (this.filters.orderable) {
        query.orderable = this.filters.orderable.toString();
      }

      // Update URL without causing navigation
      this.$router
        .replace({
          name: this.$route.name,
          params: this.$route.params,
          query,
        })
        .catch(() => {
          // Ignore navigation duplicated error
        });
    },
    async filterServices() {
      this.initLoad = true;
      try {
        await this.getCatalog({ filters: this.filters, setServiceDesign: false });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.initLoad = false;
      }
    },
    hasWizard(service) {
      return this.wizardCategories.includes(service.categoryId);
    },
    goToService(service, view) {
      this.$router.push({
        name: 'service',
        params: { id: service.categoryId, pageView: view },
      });
    },
    async load() {
      this.initLoad = true;
      try {
        await this.getCatalog({ filters: this.filters, setServiceDesign: true });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.initLoad = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.showBanner {
.services-page {
  padding: 80px 30px 30px !important;
}
}

.long-desc {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
<style lang="scss">
.widget-section .external-dns-table thead tr:first-child th {
  background-color: white;
}
.service-holder {
  border-radius: 15px;
}
.start-btn {
  font-size: 30px;
  text-transform: capitalize !important;
}
.btn-group {
  margin: 0 -3px;
}
.service-action-btn {
  margin: 0 3px;
}
.service-action-btn:before {
  border: 2px solid var(--q-primary) !important;
}
.service-description {
  max-width: 480px;
}

.loading {
  position: fixed;
  left: 88px;
  top: 200px;
  right: 30px;
  bottom: 0;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.infra-table {
  .display-name-cell {
    display: flex;
    align-items: center;
    .icon-service {
      display: flex;
      min-width: 45px;
      height: 45px;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
      border-radius: 50%;
    }
  }
}
.design-open {
  .filter-holder {
    right: 500px;
  }
  .loading {
    right: 510px;
  }
}
</style>
